import { useState, useEffect } from 'react';
import { X, ShieldCheck, Copy, Check, AlertTriangle } from 'lucide-react';
import { setupMfa, verifyMfaSetup, disableMfa, type MfaSetupResponse } from '../../services/mfaApi';

interface MfaSetupModalProps {
  onClose: () => void;
  onComplete: () => void;
}

type Step = 'scan' | 'verify' | 'backup-codes' | 'done';

export function MfaSetupModal({ onClose, onComplete }: MfaSetupModalProps) {
  const [step, setStep] = useState<Step>('scan');
  const [setupData, setSetupData] = useState<MfaSetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // Initiate setup on mount
  useEffect(() => {
    setupMfa()
      .then(setSetupData)
      .catch((err) => setError(err.response?.data?.error || 'Failed to initialize MFA setup'));
  }, []);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setupData) return;
    setError('');
    setLoading(true);

    try {
      const result = await verifyMfaSetup(setupData.secret, code);
      setBackupCodes(result.backupCodes);
      setStep('backup-codes');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid code. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCodes = async () => {
    const text = backupCodes.join('\n');
    await navigator.clipboard.writeText(text);
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  const handleDone = () => {
    onComplete();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md bg-ems-bg-card rounded-2xl border border-ems-border mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-ems-border">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-ems-green" />
            <h2 className="text-lg font-semibold text-ems-text-primary">Enable MFA</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-ems-bg-card-hover text-ems-text-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-ems-red/10 border border-ems-red/20 text-ems-red text-sm">
              {error}
            </div>
          )}

          {(step === 'scan' || step === 'verify') && (
            <>
              {/* QR Code */}
              <div className="text-center mb-6">
                <p className="text-sm text-ems-text-secondary mb-4">
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </p>
                {setupData?.qrCodeDataUrl ? (
                  <img
                    src={setupData.qrCodeDataUrl}
                    alt="MFA QR Code"
                    className="w-48 h-48 mx-auto border border-ems-border rounded-lg"
                  />
                ) : (
                  <div className="w-48 h-48 mx-auto border border-ems-border rounded-lg flex items-center justify-center text-ems-text-muted text-sm">
                    Loading...
                  </div>
                )}
                {setupData?.secret && (
                  <p className="mt-3 text-xs text-ems-text-muted">
                    Manual entry key: <code className="bg-ems-bg px-1.5 py-0.5 rounded text-xs">{setupData.secret}</code>
                  </p>
                )}
              </div>

              {/* Verify Code */}
              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ems-text-primary mb-1.5">
                    Enter the 6-digit code from your app
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-3 py-2.5 rounded-lg bg-ems-bg border border-ems-border text-ems-text-primary text-center text-lg tracking-widest placeholder:text-base placeholder:tracking-normal outline-none transition-all focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
                    placeholder="000000"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || code.length < 6}
                  className="w-full py-2.5 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 font-semibold transition-colors hover:bg-ems-forest/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Verify & Enable'}
                </button>
              </form>
            </>
          )}

          {step === 'backup-codes' && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-ems-amber/10 border border-ems-amber/20">
                <AlertTriangle className="w-5 h-5 text-ems-amber flex-shrink-0 mt-0.5" />
                <p className="text-sm text-ems-amber">
                  Save these backup codes in a safe place. Each code can only be used once. You will not be able to see them again.
                </p>
              </div>

              <div className="bg-ems-bg border border-ems-border rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((c) => (
                    <code key={c} className="text-sm text-ems-text-primary font-mono bg-ems-bg-card px-2 py-1 rounded border border-ems-border text-center">
                      {c}
                    </code>
                  ))}
                </div>
              </div>

              <button
                onClick={handleCopyCodes}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-ems-border text-sm text-ems-text-secondary hover:bg-ems-bg-card-hover"
              >
                {copiedCodes ? (
                  <><Check className="w-4 h-4 text-ems-green" /> Copied</>
                ) : (
                  <><Copy className="w-4 h-4" /> Copy all codes</>
                )}
              </button>

              <button
                onClick={handleDone}
                className="w-full py-2.5 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 font-semibold transition-colors hover:bg-ems-forest/80"
              >
                I've saved my codes
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface MfaDisableModalProps {
  onClose: () => void;
  onComplete: () => void;
}

export function MfaDisableModal({ onClose, onComplete }: MfaDisableModalProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await disableMfa(code);
      onComplete();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm bg-ems-bg-card rounded-2xl border border-ems-border mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ems-border">
          <h2 className="text-lg font-semibold text-ems-text-primary">Disable MFA</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-ems-bg-card-hover text-ems-text-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleDisable} className="p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-ems-red/10 border border-ems-red/20 text-ems-red text-sm">
              {error}
            </div>
          )}

          <p className="text-sm text-ems-text-secondary">
            Enter a verification code from your authenticator app to confirm disabling MFA.
          </p>

          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="w-full px-3 py-2.5 rounded-lg bg-ems-bg border border-ems-border text-ems-text-primary text-center text-lg tracking-widest placeholder:text-base placeholder:tracking-normal outline-none transition-all focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
            placeholder="000000"
            required
          />

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded-lg border border-ems-border text-sm text-ems-text-secondary hover:bg-ems-bg-card-hover"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || code.length < 6}
              className="flex-1 py-2 rounded-lg bg-ems-red/80 text-white text-sm font-medium hover:bg-ems-red disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Disabling...' : 'Disable MFA'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
